import React, { Fragment, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import {
  getApplicationParameters,
  updateApplicationParameter,
  getAnthropicKeyStatus,
  setAnthropicKey,
  clearDashboardCache,
} from "./applicationParametersApi";
import { runAllocationBackfill, getPeriodLock, setPeriodLock } from "../cost-allocation/api/costModelApi.js";
import ReconstructButton from "./ReconstructButton";

const ParameterRow = ({ param }) => {
  const [value, setValue] = useState(String(param.value));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setValue(String(param.value)); }, [param.value]);

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const num = parseFloat(value);
    if (Number.isNaN(num)) { setError("Enter a number."); return; }
    if (param.min != null && num < param.min) { setError(`Value must be at least ${param.min}.`); return; }
    setSaving(true);
    try {
      await updateApplicationParameter(param.key, num);
      setSaved(true);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (param.type === "bool") {
    const checked = Number(param.value) === 1;
    return (
      <div className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
        <label htmlFor={param.key} className="flex items-center gap-2 text-sm font-medium text-text">
          <input
            id={param.key}
            type="checkbox"
            checked={checked}
            disabled={saving}
            onChange={async (e) => {
              setError(null); setSaved(false); setSaving(true);
              try { await updateApplicationParameter(param.key, e.target.checked ? 1 : 0); setSaved(true); }
              catch { setError("Failed to save. Please try again."); }
              finally { setSaving(false); }
            }}
          />
          {param.label}
        </label>
        <p className="text-xs text-text-2 mt-1">{param.description}</p>
        {saved && <span className="text-xs text-success">Saved</span>}
        {error && <p className="text-xs text-danger mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
      <label htmlFor={param.key} className="block text-sm font-medium text-text">
        {param.label}
      </label>
      <p className="text-xs text-text-2 mt-1 mb-3">{param.description}</p>
      <div className="flex items-center gap-2">
        {param.unit && <span className="text-text-2 text-sm">{param.unit}</span>}
        <input
          id={param.key}
          type="number"
          value={value}
          onChange={(e) => { setValue(e.target.value); setSaved(false); }}
          className="border border-border rounded-ctrl px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-1.5 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {saved && <span className="text-xs text-success">Saved</span>}
      </div>
      <p className="text-xs text-text-2 mt-2">Default: {param.default}{param.unit ? ` ${param.unit}` : ""}</p>
      {error && <p className="text-xs text-danger mt-1">{error}</p>}
    </div>
  );
};

// BackfillCard triggers an allocation backfill. The nightly scheduler only allocates the most
// recent day, so this lets an admin populate history (e.g. after onboarding or changing rules).
// The run is async on the backend; we just confirm it started and point to the activity log.
const BackfillCard = () => {
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState(null);

  const handleRun = async () => {
    if (!window.confirm(
      "Run an allocation backfill for the last 12 months? This recomputes allocation for every day in that window and can take a few minutes. It runs in the background."
    )) return;
    setError(null);
    setStarted(false);
    setRunning(true);
    try {
      await runAllocationBackfill({});
      setStarted(true);
    } catch {
      setError("Could not start the backfill. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
      <div className="text-sm font-medium text-text">Backfill allocation</div>
      <p className="text-xs text-text-2 mt-1 mb-3">
        Recompute cost allocation across all dimensions for the last 12 months. The nightly run
        only covers the most recent day, so use this to populate history after onboarding or
        changing ownership rules. Runs in the background.
      </p>
      <button
        type="button"
        onClick={handleRun}
        disabled={running}
        className="px-4 py-1.5 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-40"
      >
        {running ? "Starting…" : "Run backfill (last 12 months)"}
      </button>
      {started && (
        <p className="text-xs text-success mt-2">
          Backfill started — track progress in the{" "}
          <Link to="/data-pipeline" className="underline">activity log</Link>. Figures update when it finishes.
        </p>
      )}
      {error && <p className="text-xs text-danger mt-2">{error}</p>}
    </div>
  );
};

// ClosePeriodCard freezes allocations on or before a date (period lock). This is a finance-close
// governance action that spans every dimension, so it lives here in admin maintenance rather than
// on the structure screen — that screen only surfaces the lock status as read-only context.
const ClosePeriodCard = () => {
  const queryClient = useQueryClient();
  const { data: lockedThrough } = useQuery(["period-lock"], () => getPeriodLock());
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isReopen = lockedThrough && date && date < lockedThrough;

  const handleApply = async () => {
    if (!date) return;
    if (!window.confirm(isReopen
      ? `Re-open the period back to ${date}? Closed months become editable again and may be re-allocated.`
      : `Close all periods through ${date}? Allocations on those days become final and can't change.`)) return;
    setError(null);
    setBusy(true);
    try {
      await setPeriodLock(date);
      queryClient.invalidateQueries(["period-lock"]);
    } catch {
      setError("Could not update the period lock. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
      <div className="text-sm font-medium text-text">Close period</div>
      <p className="text-xs text-text-2 mt-1 mb-3">
        Freeze allocations on or before a date so a closed fiscal period can't change. Nightly runs
        and backfills skip locked days, and new ownership rules can't take effect inside them. Moving
        the date earlier re-opens a period.
      </p>
      {lockedThrough ? (
        <p className="text-xs text-text-2 mb-3">
          Currently locked through <span className="font-medium text-text">{lockedThrough}</span>.
        </p>
      ) : (
        <p className="text-xs text-text-2 mb-3">No period is currently closed.</p>
      )}
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Close period through date"
          className="border border-border rounded-ctrl px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={busy || !date}
          className="px-4 py-1.5 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-40"
        >
          {busy ? "Saving…" : isReopen ? "Re-open period" : "Close period"}
        </button>
      </div>
      {error && <p className="text-xs text-danger mt-2">{error}</p>}
    </div>
  );
};

// AnthropicKeyCard configures the platform Anthropic API key that powers the AI-written executive
// summary on the Financial Control dashboard. Write-only: the value is never read back, only a
// "configured" status. Saving hot-reloads the summary client server-side (no restart).
const AnthropicKeyCard = () => {
  const queryClient = useQueryClient();
  const { data: status } = useQuery(["anthropic-key-status"], () => getAnthropicKeyStatus());
  const configured = Boolean(status?.configured);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!value.trim()) { setError("Enter the API key."); return; }
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await setAnthropicKey(value.trim());
      setValue("");
      setSaved(true);
      queryClient.invalidateQueries(["anthropic-key-status"]);
    } catch {
      setError("Could not save the key. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
      <div className="text-sm font-medium text-text">Anthropic API key — executive summaries</div>
      <p className="text-xs text-text-2 mt-1 mb-3">
        Powers the AI-written executive summary on the Financial Control dashboard. Stored
        encrypted and never shown again; leaving it unset falls back to the deterministic bullet
        summary. Saving takes effect immediately.
      </p>
      <p className="text-xs mb-3">
        Status:{" "}
        <span className={configured ? "text-success font-medium" : "text-text-2"}>
          {configured ? "Configured" : "Not configured"}
        </span>
      </p>
      <div className="flex items-center gap-2">
        <input
          type="password"
          value={value}
          onChange={(e) => { setValue(e.target.value); setSaved(false); }}
          placeholder={configured ? "Enter a new key to replace" : "sk-ant-…"}
          aria-label="Anthropic API key"
          autoComplete="off"
          className="border border-border rounded-ctrl px-3 py-1.5 text-sm w-72 focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={busy}
          className="px-4 py-1.5 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-40"
        >
          {busy ? "Saving…" : "Save key"}
        </button>
        {saved && <span className="text-xs text-success">Saved</span>}
      </div>
      {error && <p className="text-xs text-danger mt-2">{error}</p>}
    </div>
  );
};


// ClearDashboardCacheCard drops this tenant's cached dashboard payloads.
//
// The cache is normally self-invalidating: its epoch key moves whenever an allocation rule or
// budget changes or the day rolls over, and a period-aware TTL backstops the rest. This is the
// escape hatch for what the epoch cannot see — a forecast table rebuilt underneath an epoch
// that did not move, which leaves a dashboard confidently serving a superseded figure.
//
// It confirms first, and the confirm names the cost. Clearing forces the next dashboard load to
// recompute from BigQuery, and billing the customer for an accidental click is a poor look in a
// product whose whole job is controlling cloud spend.
const ClearDashboardCacheCard = () => {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleClear = async () => {
    if (!window.confirm(
      "Clear this tenant's cached dashboards? The next load of each dashboard recomputes from BigQuery, which costs money."
    )) return;
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const res = await clearDashboardCache();
      setResult(Number(res?.cleared) || 0);
      // Mark the dashboards stale too, so a tab that already holds the payload we just deleted
      // server-side refetches instead of showing it indefinitely.
      queryClient.invalidateQueries(["financial-control-summary"]);
    } catch {
      setError("Could not clear the dashboard cache. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
      <div className="text-sm font-medium text-text">Clear dashboard cache</div>
      <p className="text-xs text-text-2 mt-1 mb-3">
        Drops the cached Control Center and Ownership payloads for this tenant so the next visit
        recomputes them. Use this when a dashboard is showing a figure you know has been
        superseded. Recomputing runs fresh BigQuery jobs, so clear it when you need it rather
        than routinely.
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleClear}
          disabled={busy}
          className="border border-solid border-border box-border rounded-ctrl px-3 py-1.5 text-sm font-medium text-text bg-card hover:border-primary hover:text-primary disabled:opacity-50"
        >
          {busy ? "Clearing…" : "Clear dashboard cache"}
        </button>
        {result != null && (
          <span className="text-xs text-text-2">
            {result === 0 ? "Nothing to clear — the cache was already empty." : `Cleared ${result} cached ${result === 1 ? "dashboard" : "dashboards"}.`}
          </span>
        )}
      </div>
      {error && <p className="text-xs text-danger mt-2">{error}</p>}
    </div>
  );
};

// ReconstructCard wraps the ReconstructButton in a card matching the Maintenance section style.
// It lets an operator trigger a rebuild of the cold-start pooled conformal forecast matrix
// (used for accounts/series with < 12 months of history that cannot be calibrated individually).
const ReconstructCard = () => (
  <div className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
    <div className="text-sm font-medium text-text">Rebuild cold-start forecast matrix</div>
    <p className="text-xs text-text-2 mt-1 mb-3">
      Reconstructs the pooled conformal forecast matrix used for series with insufficient history
      (&lt; 12 months). Run this after onboarding a new customer or when cold-start coverage
      looks stale. The rebuild is synchronous and typically finishes in a few seconds.
    </p>
    <ReconstructButton />
  </div>
);

const ApplicationParametersPage = () => {
  const { data, isLoading, error } = useQuery(["application-parameters"], () => getApplicationParameters());
  const params = data || [];

  return (
    <Fragment>
      <AdministrationHeader title="Application Parameters" showPeriodSelector={false} />
      <div className="text-left flex flex-auto flex-col ml-10 mt-6">
        <p className="text-sm text-text-2 mb-4 max-w-2xl">
          Tune application behaviour. Changes apply to your account.
        </p>
        {isLoading && <div className="text-text-2 text-sm">Loading…</div>}
        {error && <div className="text-danger text-sm">Error loading parameters</div>}
        {!isLoading && !error && params.map((p) => <ParameterRow key={p.key} param={p} />)}

        <h3 className="text-sm font-semibold text-text mt-6 mb-3">Integrations</h3>
        <AnthropicKeyCard />

        <h3 className="text-sm font-semibold text-text mt-6 mb-3">Maintenance</h3>
        <BackfillCard />
        <ReconstructCard />
        <ClosePeriodCard />
        <ClearDashboardCacheCard />
      </div>
    </Fragment>
  );
};

export default ApplicationParametersPage;
